import { supabase } from "@/integrations/supabase/client";
import { photoService } from "@/services/photoService";
import { voiceMessageService } from "@/services/voiceMessageService";
import type { GalleryDataAdapter, EventData, Photo, UploadResult, ScavengerHuntCard, ScavengerHuntData, ScavengerHuntItem } from "@/components/gallery/types";

type SupabaseGalleryAdapterDeps = {
  photoService: Pick<typeof photoService, "fetchApproved" | "fetchAll" | "fetchIdsByUploader" | "uploadPhoto" | "deletePhoto" | "approve" | "unapprove">;
  voiceMessageService: Pick<typeof voiceMessageService, "fetchByViewer" | "uploadMessage" | "deleteMessage">;
};

const PHOTO_SELECT =
  "id, file_url, file_path, created_at, uploaded_by, approved, media_type, thumbnail_url, duration_seconds, original_file_path, google_drive_file_id, caption";

type TagWithPhoto = {
  item_id: string | null;
  item_index: number;
  device_code: string | null;
  photo_id: string;
  photos: Photo | Photo[] | null;
};

function normalizeJoinedPhoto(value: Photo | Photo[] | null | undefined): Photo | null {
  if (!value) return null;
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

async function fetchScavengerHuntDefinition(eventId: string): Promise<ScavengerHuntData | null> {
  const { data: hunt } = await supabase
    .from("event_scavenger_hunts")
    .select("id, template_id, custom_items, scavenger_hunt_templates(items)")
    .eq("event_id", eventId)
    .eq("is_active", true)
    .maybeSingle();

  if (!hunt) return null;

  const rawItems: ScavengerHuntItem[] = hunt.custom_items
    ? (hunt.custom_items as ScavengerHuntItem[])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    : ((hunt as any).scavenger_hunt_templates?.items ?? []);

  return {
    huntId: hunt.id,
    templateId: hunt.template_id ?? null,
    items: (rawItems as ScavengerHuntItem[]).sort((a, b) => a.order - b.order),
  } satisfies ScavengerHuntData;
}

async function generateUniqueCode(): Promise<string> {
  for (let attempt = 0; attempt < 20; attempt++) {
    const code = String(Math.floor(100000 + Math.random() * 900000));
    const { data } = await supabase.from("device_codes").select("code").eq("code", code).single();
    if (!data) return code;
  }
  throw new Error("Could not generate unique code");
}

const DEVICE_CODE_KEY_PREFIX = "device-code-";

export const createSupabaseGalleryAdapter = (deps: SupabaseGalleryAdapterDeps): GalleryDataAdapter => ({
  async fetchEvent(slug, token) {
    const { data, error } = await supabase.rpc("get_public_event", {
      p_slug: slug,
      ...(token ? { p_token: token } : {}),
    });
    if (error || !data || data.length === 0) return null;
    const row = data[0];
    return row as EventData;
  },

  async verifyPin(eventId, pin) {
    const { data, error } = await supabase.rpc("verify_event_pin", {
      p_event_id: eventId,
      p_pin: pin,
    });
    if (error) return false;
    return !!data;
  },

  async getOrCreateDeviceCode(eventId) {
    const storageKey = DEVICE_CODE_KEY_PREFIX + eventId;
    const existing = localStorage.getItem(storageKey);
    if (existing && /^\d{6}$/.test(existing)) {
      const { data } = await supabase.from("device_codes").select("code").eq("code", existing).single();
      if (data) return existing;
    }
    const code = await generateUniqueCode();
    await supabase.from("device_codes").insert({ code, event_id: eventId });
    localStorage.setItem(storageKey, code);
    return code;
  },

  async fetchGuestName(deviceCode) {
    const { data } = await supabase.from("device_codes").select("guest_name").eq("code", deviceCode).maybeSingle();
    return data?.guest_name ?? null;
  },

  async updateGuestName(deviceCode, name) {
    await supabase.from("device_codes").update({ guest_name: name }).eq("code", deviceCode);
  },

  async checkAddon(eventId, addonType) {
    const { data } = await supabase.from("event_addons").select("id").eq("event_id", eventId).eq("addon_type", addonType).maybeSingle();
    return !!data;
  },

  async checkEventAdmin(eventId) {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) return false;
    const { data, error } = await supabase.rpc("is_event_admin", {
      _user_id: userData.user.id,
      _event_id: eventId,
    });
    if (error) return false;
    return !!data;
  },

  async countApprovedPhotos(eventId) {
    const { count } = await supabase
      .from("photos")
      .select("id", { count: "exact", head: true })
      .eq("event_id", eventId)
      .eq("approved", true);
    return count ?? 0;
  },

  async fetchApprovedPhotos(eventId, opts) {
    return deps.photoService.fetchApproved(eventId, opts);
  },

  async countAllPhotos(eventId) {
    const rows = await deps.photoService.fetchAll(eventId);
    return rows.length;
  },

  async fetchAllPhotos(eventId) {
    return deps.photoService.fetchAll(eventId);
  },

  async fetchPhotoIdsByUploader(deviceCode, eventId) {
    return deps.photoService.fetchIdsByUploader(deviceCode, eventId);
  },

  async uploadPhoto(file, eventSlug, eventId, deviceCode, caption): Promise<UploadResult | null> {
    const result = await deps.photoService.uploadPhoto(file, eventSlug, eventId, deviceCode, caption);
    return result;
  },

  async updatePhotoCaption(photoId, caption) {
    await supabase.from("photos").update({ caption }).eq("id", photoId);
  },

  async deletePhoto(photoId, filePath) {
    await deps.photoService.deletePhoto(photoId, filePath);
  },

  async approvePhoto(photoId) {
    await deps.photoService.approve(photoId);
  },

  async unapprovePhoto(photoId) {
    await deps.photoService.unapprove(photoId);
  },

  async fetchLikes(photoIds, deviceCode) {
    if (photoIds.length === 0) return { counts: {}, myLikes: [] };
    const { data } = await supabase.from("photo_likes").select("photo_id, device_code").in("photo_id", photoIds);
    const counts: Record<string, number> = {};
    const myLikes: string[] = [];
    if (data) {
      data.forEach((l) => {
        counts[l.photo_id] = (counts[l.photo_id] || 0) + 1;
        if (l.device_code === deviceCode) myLikes.push(l.photo_id);
      });
    }
    return { counts, myLikes };
  },

  async toggleLike(photoId, deviceCode, currentlyLiked) {
    if (currentlyLiked) {
      await supabase.from("photo_likes").delete().eq("photo_id", photoId).eq("device_code", deviceCode);
    } else {
      await supabase.from("photo_likes").insert({ photo_id: photoId, device_code: deviceCode });
    }
  },

  async fetchGuestNames(eventId, codes) {
    if (codes.length === 0) return {};
    const { data } = await supabase.from("device_codes").select("code, guest_name").in("code", codes);
    const names: Record<string, string | null> = {};
    data?.forEach((d) => { names[d.code] = d.guest_name; });
    return names;
  },

  async fetchVoiceMessages(eventId, opts) {
    return deps.voiceMessageService.fetchByViewer(eventId, opts);
  },

  async uploadVoiceMessage(params) {
    return deps.voiceMessageService.uploadMessage(params);
  },

  async deleteVoiceMessage(id) {
    await deps.voiceMessageService.deleteMessage(id);
  },

  subscribePhotos() {
    // Temporary compatibility no-op until realtime subscriptions are reintroduced.
    return () => {};
  },

  subscribeVoice() {
    // Temporary compatibility no-op until realtime subscriptions are reintroduced.
    return () => {};
  },

  async fetchScavengerHunt(eventId) {
    return fetchScavengerHuntDefinition(eventId);
  },

  async fetchScavengerHuntCards(eventId, huntId, deviceCode) {
    const [hunt, approvedTags, myTags] = await Promise.all([
      fetchScavengerHuntDefinition(eventId),
      supabase
        .from("photo_scavenger_tags")
        .select(`item_id, item_index, photo_id, device_code, photos!inner(${PHOTO_SELECT})`)
        .eq("event_id", eventId)
        .eq("hunt_id", huntId)
        .eq("photos.approved", true),
      supabase
        .from("photo_scavenger_tags")
        .select(`item_id, item_index, photo_id, device_code, photos!inner(${PHOTO_SELECT})`)
        .eq("event_id", eventId)
        .eq("hunt_id", huntId)
        .eq("device_code", deviceCode),
    ]);

    if (!hunt) return [];

    const totalCounts: Record<string, number> = {};
    const previewPhotosByItem = new Map<string, Photo[]>();
    ((approvedTags.data ?? []) as TagWithPhoto[]).forEach((row) => {
      if (!row.item_id) return;
      totalCounts[row.item_id] = (totalCounts[row.item_id] ?? 0) + 1;
      const photo = normalizeJoinedPhoto(row.photos);
      if (!photo) return;
      const itemPhotos = previewPhotosByItem.get(row.item_id) ?? [];
      itemPhotos.push(photo);
      previewPhotosByItem.set(row.item_id, itemPhotos);
    });

    const myPhotos = new Map<string, Photo>();
    ((myTags.data ?? []) as TagWithPhoto[]).forEach((row) => {
      if (!row.item_id) return;
      const photo = normalizeJoinedPhoto(row.photos);
      if (!photo) return;
      const existing = myPhotos.get(row.item_id);
      if (!existing || new Date(photo.created_at).getTime() > new Date(existing.created_at).getTime()) {
        myPhotos.set(row.item_id, photo);
      }
    });

    return hunt.items.map((item) => ({
      item,
      myPhoto: myPhotos.get(item.id) ?? null,
      totalCount: totalCounts[item.id] ?? 0,
      previewPhotos: (previewPhotosByItem.get(item.id) ?? [])
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 4),
    })) satisfies ScavengerHuntCard[];
  },

  async fetchScavengerCategoryPhotos({ eventId, huntId, itemId, isEventAdmin = false }) {
    const query = supabase
      .from("photo_scavenger_tags")
      .select(`photo_id, photos!inner(${PHOTO_SELECT})`)
      .eq("event_id", eventId)
      .eq("hunt_id", huntId)
      .eq("item_id", itemId);

    if (!isEventAdmin) {
      query.eq("photos.approved", true);
    }

    const { data } = await query;
    const photos = ((data ?? []) as Array<{ photos: Photo | Photo[] | null }>)
      .map((row) => normalizeJoinedPhoto(row.photos))
      .filter((photo): photo is Photo => !!photo)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return photos;
  },

  async replaceScavengerHuntPhoto({ photoId, eventId, huntId, itemId, itemOrder, deviceCode }) {
    const { data: existingTags } = await supabase
      .from("photo_scavenger_tags")
      .select(`id, photo_id, device_code, photos!inner(id, file_path, created_at)`)
      .eq("event_id", eventId)
      .eq("hunt_id", huntId)
      .eq("item_id", itemId)
      .eq("device_code", deviceCode);

    await supabase.from("photo_scavenger_tags").upsert(
      {
        photo_id: photoId,
        event_id: eventId,
        hunt_id: huntId,
        item_id: itemId,
        item_index: itemOrder,
        device_code: deviceCode,
      },
      { onConflict: "photo_id,hunt_id,item_id" },
    );

    let replacedPhotoId: string | null = null;
    for (const row of (existingTags ?? []) as Array<{ photo_id: string; photos: { file_path: string } | { file_path: string }[] | null }>) {
      if (row.photo_id === photoId) continue;
      const existingPhoto = Array.isArray(row.photos) ? row.photos[0] : row.photos;
      if (!existingPhoto?.file_path) continue;
      replacedPhotoId = row.photo_id;
      await deps.photoService.deletePhoto(row.photo_id, existingPhoto.file_path);
    }

    return { replacedPhotoId };
  },

  async tagPhotoForHunt({ photoId, eventId, huntId, itemId, itemOrder, deviceCode }) {
    await supabase.from("photo_scavenger_tags").upsert(
      { photo_id: photoId, event_id: eventId, hunt_id: huntId, item_id: itemId, item_index: itemOrder, device_code: deviceCode },
      { onConflict: "photo_id,hunt_id,item_id" },
    );
  },

  async fetchScavengerTagCounts(eventId, huntId) {
    const { data } = await supabase
      .from("photo_scavenger_tags")
      .select("item_id")
      .eq("event_id", eventId)
      .eq("hunt_id", huntId);
    const counts: Record<string, number> = {};
    data?.forEach((row) => {
      if (!row.item_id) return;
      counts[row.item_id] = (counts[row.item_id] ?? 0) + 1;
    });
    return counts;
  },
});

export const supabaseGalleryAdapter = createSupabaseGalleryAdapter({ photoService, voiceMessageService });
